/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Grid from "components/generals/Grid";
import Box from "components/generals/Box";
import { FormProps } from "interfaces";
import { ProviderInterface, PersonInterface, UserInterface, UserRolesEnum, HeaderEnum } from "medicalsystem-shared";
import { FC, useMemo, useState, useEffect } from "react";
import DoctorForm from "./DoctorForm";
import PersonForm from "../PersonForm";
import UserForm from "../UserForm";
import { getValidPersonModel, getValidUserModel } from "utils/model.utils";
import { H1 } from "components/generals/_Titles";
import FormActionButtons from "../custom/FormActionButtons";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";

/**
 * Props for the DoctorFullForm component.
 * @interface DoctorFullFormProps
 * @extends {FormProps<ProviderInterface>}
 * @property {boolean} isDoctor - Flag indicating if the provider is a doctor (vs nurse)
 */
interface DoctorFullFormProps extends FormProps<ProviderInterface> {
  isDoctor: boolean;
}

/**
 * Comprehensive form for creating/editing doctor/provider information.
 * Combines three sub-forms: PersonForm (demographics), UserForm (credentials), and DoctorForm (licenses).
 * Manages state synchronization between the three forms and the main provider model.
 * 
 * Features:
 * - PersonForm: Personal information, address, contacts, emergency contacts
 * - UserForm: Username, email, password, roles, active status
 * - DoctorForm: Medical license, specialty, DEA, NPI
 * - Cascading updates: Changes in sub-forms update the main model
 * - Form validation across all three sections
 * - List update handling for table refresh
 * - Modal and page modes
 * 
 * State Management:
 * - Extracts person and user from provider model
 * - Handles nested updates (provider.person.user)
 * - Updates provider list on successful save
 * 
 * Use Cases:
 * - Creating new doctor/provider accounts with complete profile
 * - Editing existing provider information
 * - Administrative provider management
 * 
 * @param {DoctorFullFormProps} props - Component props
 * @returns {React.ReactElement} The complete doctor profile form
 */
const DoctorFullForm: FC<DoctorFullFormProps> = ({
  model,
  isDoctor,
  action,
  isModal,
  onCloseAction,
  modelList,
  setModelList,
  ...props
}) => {
  const person = useMemo<Partial<PersonInterface>>(
    () => getValidPersonModel(model.person),
    [model]
  );

  const [providerModel, setProviderModel] = useState<ProviderInterface>();
  const [_isLoading, _error, sendRequest] = useHttp();

  useEffect(() => {
    if (model && model.id) {
      sendRequest(CoreAPI_URLs.PROVIDER_GENERAL_OPERATIONS, onGetProvider, "GET", undefined, [
        {
          header: HeaderEnum.RESOURCE_ID,
          value: model.id,
        },
      ]);
    }
  }, [model.id]);

  const onGetProvider = (provider: ProviderInterface) => {
    setProviderModel(provider);
  };

  const user = useMemo<Partial<UserInterface>>(
    () => {
      if (providerModel?.person?.user) {
        return getValidUserModel(providerModel.person.user);
      }
      return model.person ? getValidUserModel(model.person.user) : person.user;
    },
    [model, providerModel, person.user]
  );

  const handleDoctorUpdate = (updatedDoctor: Partial<ProviderInterface>) => {
    const updatedModel: Partial<ProviderInterface> = {
      ...model,
      ...updatedDoctor,
    };
    updatePatientList(updatedModel as ProviderInterface);
  };

  const handlePersonUpdate = (updatedPerson: Partial<PersonInterface>) => {
    const updatedModel: Partial<ProviderInterface> = {
      ...model,
      person: {
        ...model.person,
        ...updatedPerson,
      },
    };
    updatePatientList(updatedModel as ProviderInterface);
  };

  const handleUserUpdate = (updatedUser: Partial<UserInterface>) => {
    const updatedModel: Partial<ProviderInterface> = {
      ...model,
      person: {
        ...model.person,
        user: { 
          ...model.person.user, 
          ...updatedUser,
          auth: updatedUser.auth || model.person.user?.auth
        },
      },
    };
    updatePatientList(updatedModel as ProviderInterface);
  };

  const updatePatientList = (updatedModel: ProviderInterface) => {
    const updatedList = modelList?.map((item) =>
      item.id === updatedModel.id ? updatedModel : item
    );
    setModelList(updatedList);
  };

  return (
    <Box className="full-form-wrapper" py={0}>
      <H1 className={isModal ? "title-modal" : ""}>{isDoctor ? "Doctor" : "Nurse"}</H1>
      <Grid container justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Box className="full-form-sub-wrapper">
            <DoctorForm
              isDoctor={isDoctor}
              action={action}
              model={model}
              updateListHandler={handleDoctorUpdate}
              modelList={modelList}
              setModelList={setModelList}
            />
          </Box>
          <Box className="full-form-sub-wrapper">
            <PersonForm action={action} model={person} updateListHandler={handlePersonUpdate} comesFromTable={props.comesFromTable} firstStep={true}/>
          </Box>
          <Box className="full-form-sub-wrapper">
            <UserForm action={action} model={user} updateListHandler={handleUserUpdate} person={person} type={isDoctor ? UserRolesEnum.DOCTOR : UserRolesEnum.NURSE} modifyPassword={true}/>
          </Box>
        </Grid>
      </Grid>
      <Grid className="btn-close-modal">
        <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
      </Grid>
    </Box>
  );
};

export default DoctorFullForm;
